import React, { useState } from "react";
import { Typography, Card, Row, Col, Space, Collapse, Image, Button, Spin, Tag, Tooltip } from "antd"
import { Box, Package, PenToolIcon as Tool, Cpu, Battery, Wrench, RefreshCw, Download, PenTool, Pen, Rocket } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import BundleProductFetchModal from "components/modals/productBundle/bundleProductFetch";
import BundleProductEditDialog from "components/modals/productBundle/bundleProductEditDialog";
import useProductBundleOpenApi from "apiClients/useProductBundleOpenApiClient";
import useSalesChannelOpenApi from "apiClients/useSalesChannelOpenApiClient";
import FullScreenLoader from "components/common/loadIndicators/fullScreenLoader";
import { components } from "apiClients/base/schema";
import { useLocaleStore } from "stores/localeStore";

type ProductBundleDto = components['schemas']['ProductBundleDto'];
type ProductBundlePriceDto = components['schemas']['ProductBundlePriceDto'];

const { Title, Text, Paragraph } = Typography
const { Panel } = Collapse

const formatPrice = (value?: number) => `${Math.round(value ?? 0).toLocaleString('sv-SE')} kr`;

const hasPackageDiscount = (price?: ProductBundlePriceDto) => {
  if (!price) return false;
  const listPrice = price.listPriceIncVat ?? 0;
  const packagePrice = price.productPriceIncVat ?? 0;
  return listPrice > packagePrice;
};

const sumIncVat = (prices: ProductBundlePriceDto[] | undefined, pick: (price: ProductBundlePriceDto) => number | undefined) =>
  prices?.reduce((sum, price) => sum + (pick(price) ?? 0), 0) ?? 0;

export const BundleProductsList: React.FC = () => {
  const [fetchModalOpen, setFetchModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBundleProduct, setSelectedBundleProduct] = useState<ProductBundleDto | null>(null);
  const { getProductBundles, createProductBundle, syncExistingProductBundle, updateProductBundle, exportProductBundle, loading } = useProductBundleOpenApi();
  const { getSalesChannels } = useSalesChannelOpenApi();
  const { activeLanguage } = useLocaleStore();

  const {
      data: packageProducts,
      isLoading,
      isError,
      error,
      refetch
  } = useQuery(['bundleProducts', activeLanguage?.locale], () => getProductBundles(activeLanguage?.locale));

  const {
      data: salesChannels,
      isLoading: isLoadingChannels
  } = useQuery(['salesChannels'], () => getSalesChannels());

  const getChannelName = (channelId: number): string => {
    if (!salesChannels) return channelId.toString();
    const channel = salesChannels.find(c => c.id === channelId);
    return channel?.name || channelId.toString();
  };

  const handleCreateBundleProduct = async (articleNumber: string) => {
    await createProductBundle(articleNumber);
    await refetch();
  };

  const handleSyncProductBundle = async (id: number) => {
    await syncExistingProductBundle(id);
    await refetch();
  }

  const handleExportProductBundle = async (id: number) => {
    await exportProductBundle(id);
    await refetch();
  }

  const handleEditProductBundle = (bundleProduct: ProductBundleDto) => {
    setSelectedBundleProduct(bundleProduct);
    setEditModalOpen(true);
  };

  const handleUpdateProductBundle = async (id: number, updateData: components['schemas']['ProductBundleUpdateRequest']) => {
    await updateProductBundle(id, updateData);
    await refetch();
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: '24px' }}>
        <Text type="danger">Error loading bundle products: {(error as Error).message}</Text>
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: "24px" }}>
        <div style={{ 
          display: "flex", 
          flexDirection: "column",
          marginBottom: "16px",
          backgroundColor: "#ffffff",
          padding: "16px",
          borderRadius: "8px",
          border: "1px solid #f0f0f0"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5rem"
          }}>
            <Title level={4} style={{ margin: 0 }}>Paketprodukter</Title>
            <Button 
              type="primary"
              icon={<Box style={{ marginBottom: "-3px" }} size={16} />}
              onClick={() => setFetchModalOpen(true)}
            >
              Hämta ny paketprodukt
            </Button>
          </div>
          <Paragraph style={{ color: "#666", fontSize: "1.0rem", marginBottom: 0 }}>
            Här kan du se paketprodukter som finns i Norce. Inga redigeringar sker här utan det får man göra i Norce.
            <br />
            När man har ändrat i Norce så får man synka paketprodukten manuellt här.
          </Paragraph>
        </div>

        <Row gutter={[24, 24]}>
          {packageProducts?.map((item) => (
            <Col xs={24} sm={12} lg={8} key={item.id}>
              <Card
                style={{
                  height: "100%",
                  borderRadius: "8px",
                  border: "1px solid #f0f0f0",
                }}
                title={item.name}
                extra={
                  <>
                    <Tooltip title="Redigera paketprodukt">
                      <Button 
                        type="text" 
                        icon={<Pen size={16} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditProductBundle(item);
                        }}
                        style={{ marginRight: '4px' }}
                      />
                    </Tooltip>
                    <Tooltip title="Synkronisera från Norce">
                      <Button 
                        type="text" 
                        icon={<RefreshCw size={16} />}
                        onClick={(e) => {
                        e.stopPropagation();
                        handleSyncProductBundle(item.id ?? 0);
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Exportera till Sökmotor">
                      <Button 
                        type="text" 
                        icon={<Rocket size={16} />}
                        onClick={(e) => {
                        e.stopPropagation();
                        handleExportProductBundle(item.id ?? 0);
                        }}
                      />
                    </Tooltip>
                  </>
                }
              >
                <div style={{ marginBottom: "20px" }}>
                  <Image
                    src={item.imageUrl || "/placeholder.svg"}
                    alt={item.name}
                    width={96}
                    height={96}
                    preview={false}
                    style={{
                      objectFit: "contain",
                    }}
                  />
                </div>

                <Title level={5} style={{ marginBottom: "0.5rem", fontSize: "0.95rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                    {item.itemNo}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                        <Tag color="blue">Pris: {Math.round(item.price).toLocaleString('sv-SE')} kr</Tag>
                        <Tag color="blue">Rankingpoäng: {item.score}</Tag>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "8px", marginBottom: "16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div>
                          <span style={{fontSize: "0.85rem"}}>Försäljningskanaler:</span>
                        </div>
                        <div>
                          {item.channelIds.map((channelId) => (
                            <Tag key={channelId}>{getChannelName(channelId)}</Tag>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div>
                          <span style={{fontSize: "0.85rem"}}>Url:</span>
                        </div>
                        <div>
                          <Tag color="blue">{item.url}</Tag>
                        </div>
                      </div>
                    </div>
                </Title>

                <Collapse style={{ marginBottom: "1.0rem" }}>
                  <Panel 
                    header={
                      <div style={{ 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        color: "rgba(0, 0, 0, 0.45)",
                        cursor: "pointer"
                      }}>
                        {item.description}
                      </div>
                    } 
                    key="1"
                  >
                    <Text type="secondary" style={{ whiteSpace: 'pre-line' }}>
                      {item.description}
                    </Text>
                  </Panel>
                </Collapse>

                <div style={{ marginTop: "auto" }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <Package size={20} style={{ marginBottom: "1px" }}/>
                    <Title level={5} style={{ margin: 0 }}>
                      Innehållande produkter:
                    </Title>
                  </div>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {item.products.map((product) => {
                      const price = item.prices?.find((p) => p.productId === product.id);
                      const discounted = hasPackageDiscount(price);

                      return (
                      <div
                        key={product.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "8px",
                          border: "1px solid #f0f0f0",
                          borderRadius: "6px",
                          width: '100%'
                        }}
                      >
                        <Image
                          src={product?.images?.[0]?.url || "/placeholder.svg"}
                          alt={product?.name || ""}
                          width={48}
                          height={48}
                          preview={false}
                          style={{
                            objectFit: "contain",
                          }}
                        />

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text style={{fontWeight: "bold" }}>{product.mainItemNo + " - " + product.name}</Text>
                          {price && (
                            <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginTop: "4px", flexWrap: "wrap" }}>
                              {discounted && (
                                <Text type="secondary" delete style={{ fontSize: "0.8rem" }}>
                                  {formatPrice(price.listPriceIncVat)}
                                </Text>
                              )}
                              <Text style={{ fontSize: "0.85rem" }}>
                                {formatPrice(price.productPriceIncVat)}
                              </Text>
                            </div>
                          )}
                        </div>

                        {/* {
                          product.isPrebook && (
                            <Text style={{color: "#faad14"}}><Clock size={16} /> Förbokning</Text>
                          )
                        } */}
                      </div>
                      );
                    })}
                    {item.prices?.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "baseline",
                          padding: "4px 8px 0",
                          gap: "8px"
                        }}
                      >
                        {sumIncVat(item.prices, (p) => p.listPriceIncVat) >
                          sumIncVat(item.prices, (p) => p.productPriceIncVat) && (
                          <Text type="secondary" delete style={{ fontSize: "0.8rem" }}>
                            {formatPrice(sumIncVat(item.prices, (p) => p.listPriceIncVat))}
                          </Text>
                        )}
                        <Text strong style={{ fontSize: "0.85rem" }}>
                          {formatPrice(sumIncVat(item.prices, (p) => p.productPriceIncVat))}
                        </Text>
                      </div>
                    )}
                  </Space>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <BundleProductFetchModal
          isOpen={fetchModalOpen}
          onClose={() => setFetchModalOpen(false)}
          onFetch={handleCreateBundleProduct}
        />

        <BundleProductEditDialog
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedBundleProduct(null);
          }}
          onUpdate={handleUpdateProductBundle}
          bundleProduct={selectedBundleProduct}
        />
      </div>
      <FullScreenLoader loading={loading} message="Uppdaterar paketprodukter" />
    </>
  )
};